import apiService from './api';
import type {
  CHURCH_PRODUCT,
  Church,
  ChurchesListResponse,
  PagedListRequest,
  SocialMediaConnectResponse,
  UpdateAddress,
  UpdateGeneralInfo,
  UpdateGeneralSchoolInfo,
  UpdateGivingLinks,
  UpdateSchoolAddress,
  UpdateSchoolGivingLinks,
  UpdateSchoolSocialMedia,
  UpdateSocialMedia,
} from '@/types';
import { formatQueryString } from '@/helpers';
import { DEFAULT_LIST_LIMIT_FILTER } from '@/constants';

const API_URL = '/admin/churches';

class ChurchesService {
  getChurches = async (churchId?: string, filters?: PagedListRequest) => {
    try {
      const response = await apiService.get<ChurchesListResponse>(
        `${API_URL}/${churchId}/list${formatQueryString({
          limit: DEFAULT_LIST_LIMIT_FILTER,
          page: 1,
          ...filters,
        })}`
      );
      return response.data;
    } catch (e) {
      console.error('Error fetching churches:', e);
      throw e;
    }
  };
  getChurchById = async (id: string) => {
    try {
      const response = await apiService.get<Church>(`${API_URL}/${id}`);
      return response.data;
    } catch (e) {
      console.error('Error fetching church:', e);
      throw e;
    }
  };

  getChurchesProducts = async (churchId: string) => {
    try {
      const response = await apiService.get<
        Record<
          CHURCH_PRODUCT,
          {
            isEnabled: boolean;
            url: string;
          }
        >
      >(`${API_URL}/${churchId}/products`);
      if (!response.data || typeof response.data !== 'object') {
        return {} as Record<
          CHURCH_PRODUCT,
          {
            isEnabled: boolean;
            url: string;
          }
        >;
      }

      return response.data;
    } catch (e) {
      console.error('Error fetching church products:', e);
      throw e;
    }
  };

  updateImageSource = async (churchId: string, imageSource: 'parish' | 'diocesan') => {
    try {
      const response = await apiService.put(`${API_URL}/${churchId}/image-source`, {
        imageSource,
      });
      return response.data;
    } catch (e) {
      console.error('Error updating church image source:', e);
      throw e;
    }
  };

  updateGeneralInfo = async (id: string, data: UpdateGeneralInfo) => {
    try {
      const response = await apiService.put<Church>(`${API_URL}/${id}/general-info`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating general info:', e);
      throw e;
    }
  };

  updateAddress = async (id: string, data: UpdateAddress) => {
    try {
      const response = await apiService.put<Church>(`${API_URL}/${id}/address`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating address:', e);
      throw e;
    }
  };

  updateGeneralSchoolInfo = async (id: string, data: UpdateGeneralSchoolInfo) => {
    try {
      const response = await apiService.put<Church>(`${API_URL}/${id}/general-school-info`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating general school info:', e);
      throw e;
    }
  };

  updateSchoolAddress = async (id: string, data: UpdateSchoolAddress) => {
    try {
      const response = await apiService.put<Church>(`${API_URL}/${id}/school-address`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating school address:', e);
      throw e;
    }
  };

  updateGivingLinks = async (id: string, data: UpdateGivingLinks) => {
    try {
      const response = await apiService.put<Church>(`${API_URL}/${id}/giving-links`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating giving links:', e);
      throw e;
    }
  };

  updateSchoolGivingLinks = async (id: string, data: UpdateSchoolGivingLinks) => {
    try {
      const response = await apiService.put<Church>(`${API_URL}/${id}/school-giving-links`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating school giving links:', e);
      throw e;
    }
  };

  updateSocialMedia = async (id: string, data: UpdateSocialMedia) => {
    try {
      const response = await apiService.put<Church>(`${API_URL}/${id}/social-media`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating social media:', e);
      throw e;
    }
  };

  updateSchoolSocialMedia = async (id: string, data: UpdateSchoolSocialMedia) => {
    try {
      const response = await apiService.put<Church>(`${API_URL}/${id}/school-social-media`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating school social media:', e);
      throw e;
    }
  };

  connectFacebook = async (churchId: string): Promise<Window | null> => {
    try {
      const response = await apiService.get<SocialMediaConnectResponse>(`/admin/church/${churchId}/facebook/connect`);
      const popup = window.open(response.data.authUrl, '_blank', 'width=600,height=700');
      return popup;
    } catch (e) {
      console.error('Error connecting Facebook:', e);
      throw e;
    }
  };

  disconnectFacebook = async (churchId: string) => {
    try {
      const response = await apiService.get(`/admin/church/${churchId}/facebook/disconnect`);
      return response.data;
    } catch (e) {
      console.error('Error disconnecting Facebook:', e);
      throw e;
    }
  };

  // TODO: Twitter/X is temporarily disabled. This code may be deleted later.
  // connectTwitter = async (churchId: string): Promise<Window | null> => {
  //   try {
  //     const response = await apiService.get<SocialMediaConnectResponse>(`/admin/church/${churchId}/twitter/connect`);
  //     const popup = window.open(response.data.authUrl, '_blank', 'width=600,height=700');
  //     return popup;
  //   } catch (e) {
  //     console.error('Error connecting Twitter:', e);
  //     throw e;
  //   }
  // };

  // disconnectTwitter = async (churchId: string) => {
  //   try {
  //     const response = await apiService.get(`/admin/church/${churchId}/twitter/disconnect`);
  //     return response.data;
  //   } catch (e) {
  //     console.error('Error disconnecting Twitter:', e);
  //     throw e;
  //   }
  // };
}

export default new ChurchesService();
